import {
  Box,
  Tab,
  Tabs,
  type SxProps,
  type TabProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback, type SyntheticEvent } from "react";
import { type BenchOrderReportFieldsFragment } from "../../../generated/graphql";
import {
  MARKER_COLOR,
  orderMarker,
  orderTabLabel,
} from "../bench-report-model";

const styles = {
  tabs: { borderBottom: 1, borderColor: "divider" } satisfies SxProps<Theme>,
  label: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    textTransform: "none",
  } satisfies SxProps<Theme>,
};

const DOT_SIZE = 10;

const dotStyles: Record<string, SxProps<Theme>> = Object.fromEntries(
  Object.entries(MARKER_COLOR).map(([marker, color]) => [
    marker,
    {
      width: DOT_SIZE,
      height: DOT_SIZE,
      borderRadius: "50%",
      flexShrink: 0,
      bgcolor: color === "default" ? "grey.500" : `${color}.main`,
    },
  ]),
);

type OrderTabProps = TabProps & {
  readonly order: BenchOrderReportFieldsFragment;
};

/**
 * One order's tab. MUI `Tabs` reads `value` off each child and clones it with
 * the selection props, so the caller sets `value` and the tab forwards every
 * prop to `Tab`.
 */
const OrderTab = memo(({ order, ...tabProps }: OrderTabProps) => {
  const marker = orderMarker(order);
  return (
    <Tab
      {...tabProps}
      label={
        <Box sx={styles.label} title={marker}>
          <Box aria-label={marker} component="span" sx={dotStyles[marker]} />
          {orderTabLabel(order)}
        </Box>
      }
    />
  );
});
OrderTab.displayName = "OrderTab";

type BenchOrderTabsProps = {
  readonly orders: BenchOrderReportFieldsFragment[];
  readonly selectedOrderUuid: string | null;
  readonly onSelect: (orderUuid: string) => void;
};

/**
 * One scrollable tab per order of a report, each with a dot for the order's
 * latest outcome, so the operator sees which order needs attention without
 * opening it.
 */
const BenchOrderTabs = ({
  orders,
  selectedOrderUuid,
  onSelect,
}: BenchOrderTabsProps) => {
  const handleChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      onSelect(value);
    },
    [onSelect],
  );
  return (
    <Tabs
      allowScrollButtonsMobile
      scrollButtons="auto"
      sx={styles.tabs}
      value={selectedOrderUuid ?? false}
      variant="scrollable"
      onChange={handleChange}
    >
      {orders.map((order) => (
        <OrderTab key={order.orderUuid} order={order} value={order.orderUuid} />
      ))}
    </Tabs>
  );
};

export default BenchOrderTabs;
